import moment from "moment";
import { useMemo } from "react";

import type { Amount } from "../../../core/amount/amount";
import type { Account } from "../../account/account";
import { TimeRange } from "../search/timerange";
import type { TransactionFilters } from "./transaction-filter";
import { TransactionDirection } from "./transaction-filter";
import { useTransactions } from "./use-transactions";

export interface MonthlyFlux {
  income: Amount;
  outcome: Amount;
  loading: boolean;
}

/**
 * Income and outcome totals for the current month, the two figures the home screens show
 * side by side. Debits come back as positive values, so the outcome is forced negative:
 * next to a "+1 481,00 €" income, a "+585,00 €" outcome would read as another credit.
 */
export function useMonthlyFlux(account: Account | null): MonthlyFlux {
  const filters: TransactionFilters = useMemo(
    () => ({
      timerange: { timeRange: TimeRange.Other, bounds: [moment().startOf("month"), moment()] },
      amountRange: [null, null],
      currencyId: undefined,
      isQualified: undefined,
      isJustified: undefined,
      isLocked: undefined,
    }),
    [],
  );

  const income = useTransactions(account, TransactionDirection.INCOME, filters);
  const outcome = useTransactions(account, TransactionDirection.OUTCOME, filters);

  const total = (transactions: typeof income.data) =>
    transactions.reduce((sum, transaction) => sum + (transaction.amount?.value ?? 0), 0);

  const toAmount = (value: number): Amount =>
    ({ value, currency: account?.currency, currencyAlpha: account?.currencyAlpha }) as Amount;

  return {
    income: toAmount(total(income.data)),
    outcome: toAmount(-Math.abs(total(outcome.data))),
    loading: income.loading || outcome.loading,
  };
}
